/**
 * The Relay envelope (TRD section 3). The producer publishes it, the Relay
 * forwards it unchanged to the member's live sockets, and when nothing
 * acknowledged it the producer stores the same envelope in its undelivered
 * table for the client to collect. One shape on every path, so a client applies
 * a frame from the socket and a row from the undelivered fetch identically.
 *
 * `notification.feed` carries the `NotificationSnapshot` in clear (TRD Draft
 * 6): the Relay writes nothing, so there is nothing at rest to protect, and
 * the content crosses Cloudflare in memory for the length of one delivery.
 * `reset.offer` carries no payload at all because the prompt is baked into
 * Desktop. Every variant is strict, so a field that belongs to the other topic
 * fails parsing instead of being silently dropped, and no envelope carries a
 * top-level `title`, `body` or `kind`. There is no `seq`: the Relay numbers
 * nothing because it stores nothing.
 */
import { z } from "zod";
import { RELAY_MAX_SNAPSHOT_BYTES, RelayEventId, RelayResourceId, RelayTimestamp, } from "./primitives.js";
import { NOTIFICATION_SNAPSHOT_PAYLOAD_VERSION, NotificationSnapshot } from "./snapshot.js";
/** The longest countdown a Reset prompt may show. The configured default is 12 seconds. */
export const RELAY_MAX_RESPONSE_WINDOW_SECONDS = 120;
const common = {
    eventId: RelayEventId,
    createdAt: RelayTimestamp,
    expiresAt: RelayTimestamp.optional(),
};
/**
 * The snapshot fields. The notification id lives inside the snapshot
 * (`snapshot.notification.id` or `snapshot.notificationId`), so the envelope
 * does not repeat it and the two can never disagree.
 */
const notificationFeed = {
    topic: z.literal("notification.feed"),
    payloadVersion: z.literal(NOTIFICATION_SNAPSHOT_PAYLOAD_VERSION),
    snapshot: NotificationSnapshot,
};
/**
 * `offerId` is the `reset_offers` row id. The Desktop uses it to claim the
 * offer, to report `offered` and the decision, and in the `start_reset` bridge
 * message; the `eventId` is a UUIDv5 of it.
 */
const resetOffer = {
    topic: z.literal("reset.offer"),
    offerId: RelayResourceId,
    displayBy: RelayTimestamp,
    responseWindowSeconds: z.number().int().positive().max(RELAY_MAX_RESPONSE_WINDOW_SECONDS),
};
/** Deadlines must fall after the event was created, or the event is dead on arrival. */
function envelopeInvariants(value, ctx) {
    const created = Date.parse(value.createdAt);
    for (const field of ["expiresAt", "displayBy"]) {
        const deadline = value[field];
        if (deadline !== undefined && Date.parse(deadline) <= created) {
            ctx.addIssue({ code: "custom", path: [field], message: `${field} must be after createdAt` });
        }
    }
    if (value.snapshot !== undefined &&
        new TextEncoder().encode(JSON.stringify(value.snapshot)).byteLength > RELAY_MAX_SNAPSHOT_BYTES) {
        ctx.addIssue({
            code: "custom",
            path: ["snapshot"],
            message: `snapshot exceeds ${RELAY_MAX_SNAPSHOT_BYTES} bytes`,
        });
    }
}
export const RelayEnvelope = z
    .discriminatedUnion("topic", [
    z.strictObject({ ...common, ...notificationFeed }),
    z.strictObject({ ...common, ...resetOffer }),
])
    .superRefine(envelopeInvariants);
/** The publish body is the envelope itself; the Relay adds nothing to it. */
export const RelayPublishRequest = RelayEnvelope;
//# sourceMappingURL=envelope.js.map